'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { FolderKanban, CreditCard, TicketCheck, PlusCircle, ArrowRight, CheckCircle2, Bell, ChevronRight,  } from 'lucide-react';
import Link from 'next/link';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';


const clientStats = [
  { label: 'Active Projects', value: '3', icon: FolderKanban, color: 'text-primary', bg: 'bg-primary/10', change: '+1 this month' },
  { label: 'Open Invoices', value: '2', icon: CreditCard, color: 'text-warning', bg: 'bg-warning/10', change: '$12,400 due' },
  { label: 'Support Tickets', value: '5', icon: TicketCheck, color: 'text-cyan', bg: 'bg-cyan/10', change: '2 unresolved' },
  { label: 'Completed Projects', value: '7', icon: CheckCircle2, color: 'text-success', bg: 'bg-success/10', change: 'All time' },
];

const recentProjects = [
  { id: 'proj-001', name: 'Nexus ERP Platform', stage: 'Testing', completion: 78, deadline: 'Sep 15, 2026', priority: 'Critical' as const },
  { id: 'proj-003', name: 'BlueStar E-Commerce Portal', stage: 'Development', completion: 54, deadline: 'Oct 1, 2026', priority: 'High' as const },
  { id: 'proj-005', name: 'Meridian Cloud Migration', stage: 'Development', completion: 41, deadline: 'Sep 30, 2026', priority: 'Medium' as const },
];

const recentInvoices = [
  { id: 'INV-2026-041', project: 'Nexus ERP Platform', amount: '$8,400', due: 'Aug 20, 2026', status: 'Overdue' },
  { id: 'INV-2026-038', project: 'BlueStar E-Commerce Portal', amount: '$4,000', due: 'Sep 5, 2026', status: 'Pending' },
  { id: 'INV-2026-031', project: 'Meridian Cloud Migration', amount: '$6,200', due: 'Jul 30, 2026', status: 'Paid' },
];

const recentTickets = [
  { id: 'TKT-0091', subject: 'Login issue on staging environment', priority: 'High', status: 'In Progress', updated: '2h ago' },
  { id: 'TKT-0087', subject: 'API integration not returning correct data', priority: 'Critical', status: 'Open', updated: '1d ago' },
  { id: 'TKT-0082', subject: 'Request for additional user licenses', priority: 'Low', status: 'Resolved', updated: '3d ago' },
];

const quickActions = [
  { label: 'Request a Service', href: '/client-portal/request-service', icon: PlusCircle, color: 'text-primary', bg: 'bg-primary/10' },
  { label: 'Submit a Project', href: '/client-portal/request-service', icon: FolderKanban, color: 'text-cyan', bg: 'bg-cyan/10' },
  { label: 'Raise a Ticket', href: '/client-portal/support-tickets', icon: TicketCheck, color: 'text-warning', bg: 'bg-warning/10' },
  { label: 'View Invoices', href: '/client-portal/invoices', icon: CreditCard, color: 'text-success', bg: 'bg-success/10' },
];

const stageVariantMap: Record<string, string> = {
  Testing: 'stage-testing',
  Development: 'stage-development',
  Deployment: 'stage-deployment',
  'Client Review': 'stage-review',
  Maintenance: 'stage-maintenance',
  Proposal: 'stage-proposal',
  Approval: 'stage-approval',
};

const priorityVariantMap: Record<string, string> = {
  Critical: 'priority-critical',
  High: 'priority-high',
  Medium: 'priority-medium',
  Low: 'priority-low',
};

export default function ClientPortalPage() {
  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Client Portal</p>
            <h1 className="text-2xl font-bold text-foreground">Welcome back, James</h1>
            <p className="text-sm text-muted-foreground mt-1">Pinnacle Systems · Last login: Today at 09:14 AM</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="relative p-2 rounded-lg bg-card border border-border hover:border-primary/40 transition-colors text-muted-foreground hover:text-foreground">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" />
            </button>
            <Link href="/client-portal/request-service">
              <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                <PlusCircle size={15} />
                New Request
              </button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {clientStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="card-surface p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                    <Icon size={18} className={stat.color} />
                  </div>
                </div>
                <p className="text-metric text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                <p className="text-2xs text-muted-foreground mt-1">{stat.change}</p>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.label} href={action.href}>
                <div className="card-surface p-4 flex items-center gap-3 hover:border-primary/30 transition-colors cursor-pointer group">
                  <div className={`w-9 h-9 rounded-lg ${action.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={17} className={action.color} />
                  </div>
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{action.label}</span>
                  <ChevronRight size={14} className="ml-auto text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Projects */}
          <div className="lg:col-span-2 card-surface p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground">Active Projects</h2>
              <Link href="/client-portal/projects" className="text-xs text-primary hover:underline flex items-center gap-1">
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <div className="space-y-3">
              {recentProjects.map((proj) => (
                <div key={proj.id} className="bg-muted/30 rounded-lg p-4 border border-border/50">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{proj.name}</p>
                      <p className="text-2xs text-muted-foreground mt-0.5">Due {proj.deadline}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge label={proj.stage} variant={stageVariantMap[proj.stage] as any} />
                      <Badge label={proj.priority} variant={priorityVariantMap[proj.priority] as any} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${proj.completion}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground tabular-nums">{proj.completion}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-6">
            {/* Recent Invoices */}
            <div className="card-surface p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground">Invoices</h2>
                <Link href="/client-portal/invoices" className="text-xs text-primary hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12} />
                </Link>
              </div>
              <div className="space-y-3">
                {recentInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{inv.project}</p>
                      <p className="text-2xs text-muted-foreground">{inv.id}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-foreground">{inv.amount}</p>
                      <span className={`text-2xs font-medium ${inv.status === 'Overdue' ? 'text-danger' : inv.status === 'Paid' ? 'text-success' : 'text-warning'}`}>
                        {inv.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Tickets */}
            <div className="card-surface p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground">Support Tickets</h2>
                <Link href="/client-portal/support-tickets" className="text-xs text-primary hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12} />
                </Link>
              </div>
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <div key={ticket.id} className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${ticket.status === 'Open' ? 'bg-danger' : ticket.status === 'In Progress' ? 'bg-warning' : 'bg-success'}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground leading-snug">{ticket.subject}</p>
                      <p className="text-2xs text-muted-foreground mt-0.5">{ticket.id} · {ticket.updated}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
